import { useState, useEffect, useContext } from "react";
import api from "../api/api";
import { AuthContext } from "../context/AuthContext";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

// ─── Constantes ───────────────────────────────────────────────────────────────
const PROVINCIAS = [
  "Buenos Aires",
  "CABA",
  "Catamarca",
  "Chaco",
  "Chubut",
  "Córdoba",
  "Corrientes",
  "Entre Ríos",
  "Formosa",
  "Jujuy",
  "La Pampa",
  "La Rioja",
  "Mendoza",
  "Misiones",
  "Neuquén",
  "Río Negro",
  "Salta",
  "San Juan",
  "San Luis",
  "Santa Cruz",
  "Santa Fe",
  "Santiago del Estero",
  "Tierra del Fuego",
  "Tucumán",
];

const STATUS_CONFIG = {
  active:            { label: "Activo",           color: "#1b7f4e", bg: "#e8f7ef" },
  pending_review:    { label: "En revisión",       color: "#b06a00", bg: "#fff3e0" },
  pending_documents: { label: "Docs. pendientes",  color: "#b06a00", bg: "#fff3e0" },
  draft:             { label: "Borrador",          color: "#5c7a8a", bg: "#f0f5f8" },
  suspended:         { label: "Suspendido",        color: "#c0392b", bg: "#fdecea" },
  rejected:          { label: "Rechazado",         color: "#c0392b", bg: "#fdecea" },
  approved:          { label: "Aprobado",          color: "#1b7f4e", bg: "#e8f7ef" },
  expired:           { label: "Vencido",           color: "#7b5ea7", bg: "#f3eeff" },
};

const SERVICE_ICONS = {
  "Plomería": "🔧", "Gasista": "🔥", "Electricidad": "⚡", "Pintura": "🎨",
  "Albañilería": "🧱", "Cerrajería": "🔑", "Limpieza": "🧹", "Jardinería": "🌿",
};

const DIAS_LABELS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

// ─── Shared styles ────────────────────────────────────────────────────────────
const inputStyle = {
  padding: "11px 14px", borderRadius: 8, border: "1.5px solid #cddae4",
  fontSize: 14, fontFamily: "Montserrat, sans-serif", color: "#163244",
  background: "#fff", outline: "none", width: "100%", boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 600, color: "#163244",
  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
};

// ─── Shared components ────────────────────────────────────────────────────────
function Badge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#5c7a8a", bg: "#f0f5f8" };
  return (
    <span style={{
      padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      letterSpacing: "0.04em", color: cfg.color, background: cfg.bg, display: "inline-block",
    }}>
      {cfg.label.toUpperCase()}
    </span>
  );
}

function SectionCard({ title, subtitle, action, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 16px rgba(22,50,68,0.07)", marginBottom: 20, overflow: "hidden" }}>
      <div style={{ padding: "18px 28px", borderBottom: "1px solid #f0f5f8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#163244" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: "#8fa3b1", marginTop: 2 }}>{subtitle}</div>}
        </div>
        {action}
      </div>
      <div style={{ padding: "24px 28px" }}>{children}</div>
    </div>
  );
}

function SaveButton({ onClick, loading, saved }) {
  return (
    <button onClick={onClick} style={{
      padding: "8px 20px", borderRadius: 8,
      background: saved ? "#1b7f4e" : "#163244",
      color: "#fff", border: "none", fontFamily: "Montserrat, sans-serif",
      fontWeight: 600, fontSize: 13, cursor: "pointer",
      display: "flex", alignItems: "center", gap: 6, transition: "background 0.3s",
    }}>
      {loading
        ? <span style={{ width: 13, height: 13, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
        : saved ? "✓ Guardado" : "Guardar cambios"
      }
    </button>
  );
}

function calcHours(start, end) {
  try {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    const diff = (eh * 60 + em) - (sh * 60 + sm);
    return diff > 0 ? (diff / 60).toFixed(1) : "—";
  } catch { return "—"; }
}

function ErrorMsg({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ background: "#fdecea", border: "1px solid #e57373", borderRadius: 8, padding: "9px 14px", fontSize: 13, color: "#c0392b", fontWeight: 600, marginTop: 12 }}>
      ⚠ {msg}
    </div>
  );
}

// ─── Sección perfil ───────────────────────────────────────────────────────────
function ProfileSection({ data, onUpdate }) {
  const [form,    setForm]    = useState({ ...data });
  const [loading, setLoading] = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");

  const update = (field, val) => { setForm(f => ({ ...f, [field]: val })); setSaved(false); };

  const handleSave = async () => {
    setLoading(true); setError("");
    try {
      await api.put("/providers/me", {
        display_name: form.display_name,
        legal_name:   form.legal_name,
        dni:          form.dni,
        cuit:         form.cuit,
        phone:        form.phone,
        bio:          form.bio,
      });
      setSaved(true);
      onUpdate(form);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar. Intentá de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SectionCard title="Datos del perfil" subtitle="Información visible para los clientes" action={<SaveButton onClick={handleSave} loading={loading} saved={saved} />}>
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Nombre para mostrar *</label>
          <input style={inputStyle} value={form.display_name} onChange={e => update("display_name", e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Nombre legal</label>
          <input style={inputStyle} value={form.legal_name || ""} onChange={e => update("legal_name", e.target.value)} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>DNI</label>
          <input style={inputStyle} value={form.dni || ""} onChange={e => update("dni", e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>CUIT / CUIL</label>
          <input style={inputStyle} value={form.cuit || ""} onChange={e => update("cuit", e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Teléfono</label>
          <input style={inputStyle} value={form.phone || ""} onChange={e => update("phone", e.target.value)} />
        </div>
      </div>
      <div>
        <label style={labelStyle}>Presentación</label>
        <textarea style={{ ...inputStyle, resize: "vertical", minHeight: 90, lineHeight: 1.6 }} value={form.bio || ""} onChange={e => update("bio", e.target.value)} maxLength={500} />
        <div style={{ textAlign: "right", fontSize: 11, color: "#8fa3b1", marginTop: 2 }}>{(form.bio || "").length}/500</div>
      </div>
      <ErrorMsg msg={error} />
    </SectionCard>
  );
}

// ─── Sección datos personales (users/me) ──────────────────────────────────────
function PersonalDataSection() {
  const { setUser } = useContext(AuthContext);

  const [form,    setForm]    = useState({ first_name: "", last_name: "", username: "", phone: "", street: "", city: "", province: "", postal_code: "" });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    api.get("/users/me")
      .then(res => {
        const u = res.data.user || res.data;
        setForm({
          first_name:  u.first_name  || "",
          last_name:   u.last_name   || "",
          username:    u.username    || "",
          phone:       u.phone       || "",
          street:      u.street      || "",
          city:        u.city        || "",
          province:    u.province    || "",
          postal_code: u.postal_code || "",
        });
      })
      .catch(() => setError("No se pudo cargar los datos personales."))
      .finally(() => setLoading(false));
  }, []);

  const update = (field, val) => { setForm(f => ({ ...f, [field]: val })); setSaved(false); };

  const handleSave = async () => {
    setError("");
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError("Nombre y apellido son obligatorios.");
      return;
    }
    if (form.username && !/^[a-zA-Z0-9_]{3,20}$/.test(form.username)) {
      setError("El nombre de usuario debe tener entre 3 y 20 caracteres (letras, números y _).");
      return;
    }
    setSaving(true);
    try {
      const res = await api.put("/users/me", {
        first_name:  form.first_name.trim(),
        last_name:   form.last_name.trim(),
        username:    form.username.trim()      || undefined,
        phone:       form.phone               || undefined,
        street:      form.street.trim()       || undefined,
        city:        form.city.trim()         || undefined,
        province:    form.province.trim()     || undefined,
        postal_code: form.postal_code.trim()  || undefined,
      });
      // Refrescar el contexto global
      if (setUser) {
        const meRes = await api.get("/users/me");
        setUser(meRes.data.user || meRes.data);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      if (err.response?.status === 409) {
        setError("El nombre de usuario ya está en uso. Elegí otro.");
      } else {
        setError(err.response?.data?.error || "Error al guardar. Intentá de nuevo.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 16px rgba(22,50,68,0.07)", marginBottom: 20, padding: "28px", textAlign: "center", color: "#8fa3b1", fontSize: 13 }}>
      Cargando datos personales…
    </div>
  );

  return (
    <SectionCard
      title="Datos personales"
      subtitle="Tu información como usuario de Fixo"
      action={<SaveButton onClick={handleSave} loading={saving} saved={saved} />}
    >
      {/* Nombre y apellido */}
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Nombre *</label>
          <input style={inputStyle} value={form.first_name} onChange={e => update("first_name", e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Apellido *</label>
          <input style={inputStyle} value={form.last_name} onChange={e => update("last_name", e.target.value)} />
        </div>
      </div>

      {/* Username */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Nombre de usuario</label>
        <input
          style={inputStyle}
          value={form.username}
          onChange={e => update("username", e.target.value)}
          placeholder="3-20 caracteres — letras, números y _"
        />
      </div>

      {/* Teléfono */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Teléfono</label>
        <PhoneInput
          country="ar"
          value={form.phone}
          onChange={phone => update("phone", phone)}
          inputStyle={{ ...inputStyle, paddingLeft: 48 }}
          buttonStyle={{ borderColor: "#cddae4", borderRadius: "8px 0 0 8px", background: "#fff" }}
          containerStyle={{ width: "100%" }}
        />
      </div>

      {/* Domicilio */}
      <div style={{ borderTop: "1px solid #f0f5f8", paddingTop: 16, marginTop: 4 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#8fa3b1", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
          Domicilio <span style={{ fontWeight: 400, textTransform: "none" }}>(opcional)</span>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Calle y número</label>
          <input style={inputStyle} value={form.street} onChange={e => update("street", e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Localidad</label>
            <input style={inputStyle} value={form.city} onChange={e => update("city", e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Provincia</label>
            <select style={inputStyle} value={form.province} onChange={e => update("province", e.target.value)}>
              <option value="">— Seleccioná —</option>
              {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div style={{ width: "40%" }}>
          <label style={labelStyle}>Código postal</label>
          <input style={inputStyle} value={form.postal_code} onChange={e => update("postal_code", e.target.value)} />
        </div>
      </div>

      <ErrorMsg msg={error} />
    </SectionCard>
  );
}

// ─── Sección servicios ────────────────────────────────────────────────────────
function ServicesSection({ services: initialServices }) {
  const [services,  setServices]  = useState(initialServices);
  const [catalog,   setCatalog]   = useState([]);
  const [adding,    setAdding]    = useState(false);
  const [saving,    setSaving]    = useState(null);
  const [editing,   setEditing]   = useState({});
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState("");

  const loadCatalog = async () => {
    if (catalog.length > 0) { setAdding(true); return; }
    try {
      const { data } = await api.get("/providers/catalog");
      setCatalog(data.services || data || []);
      setAdding(true);
    } catch { setError("Error cargando catálogo"); }
  };

  const handleAdd = async (serviceId) => {
    setSaving(serviceId); setError("");
    try {
      const { data } = await api.post("/providers/me/services", { service_id: serviceId });
      setServices(s => [...s, data.service]);
      setAdding(false);
      setSuccess("Servicio agregado correctamente");
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      setError(err.response?.data?.error || "Error al agregar servicio");
    } finally { setSaving(null); }
  };

  const handleRemove = async (s) => {
    if (!window.confirm(`¿Eliminar "${s.service_name || s.label}"? Esta acción no se puede deshacer.`)) return;
    setSaving(s.id); setError("");
    try {
      await api.delete(`/providers/me/services/${s.id}`);
      setServices(sv => sv.filter(sv => sv.id !== s.id));
      setSuccess("Servicio eliminado");
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      setError(err.response?.data?.error || "Error al eliminar servicio");
    } finally { setSaving(null); }
  };

  const startEdit = (s) => {
    setEditing(e => ({ ...e, [s.id]: {
      years_experience:       s.years_experience       || 0,
      base_price_reference:   s.base_price_reference   || "",
      estimated_duration_hs:  s.estimated_duration_hs  || "",
      emergency_enabled:      s.emergency_enabled      || false,
      quote_enabled:          s.quote_enabled          !== false,
    }}));
  };

  const cancelEdit = (id) => setEditing(e => { const n = { ...e }; delete n[id]; return n; });

  const updateDraft = (id, field, val) => setEditing(e => ({ ...e, [id]: { ...e[id], [field]: val } }));

  const handleSaveEdit = async (s) => {
    setSaving(s.id); setError("");
    try {
      const { data } = await api.patch(`/providers/me/services/${s.id}`, editing[s.id]);
      setServices(sv => sv.map(sv => sv.id === s.id ? { ...sv, ...data.service } : sv));
      cancelEdit(s.id);
      setSuccess("Servicio actualizado correctamente");
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar cambios");
    } finally { setSaving(null); }
  };

  const registeredIds = new Set(services.map(s => s.service_id));

  return (
    <SectionCard title="Mis servicios" subtitle="Configurá cada rubro que ofrecés"
      action={<button onClick={loadCatalog} style={{ padding: "8px 16px", borderRadius: 8, border: "2px solid #163244", background: "transparent", color: "#163244", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>+ Agregar servicio</button>}>
      {error   && <div style={{ background: "#fdecea", border: "1px solid #e57373", borderRadius: 8, padding: "9px 14px", fontSize: 13, color: "#c0392b", fontWeight: 600, marginBottom: 12 }}>⚠ {error}</div>}
      {success && <div style={{ background: "#e8f7ef", border: "1px solid #1b7f4e", borderRadius: 8, padding: "9px 14px", fontSize: 13, color: "#1b7f4e", fontWeight: 600, marginBottom: 12 }}>✓ {success}</div>}

      {adding && (
        <div style={{ background: "#fffbf2", border: "2px dashed #E7A432", borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#163244", marginBottom: 10 }}>Seleccioná un servicio para agregar:</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {catalog.filter(c => !registeredIds.has(c.id)).map(c => (
              <button key={c.id} onClick={() => handleAdd(c.id)} disabled={saving === c.id}
                style={{ padding: "6px 14px", borderRadius: 20, background: "#163244", border: "none", color: "#fff", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                {saving === c.id ? "⏳" : `${SERVICE_ICONS[c.service_name] || "🛠️"} ${c.service_name}`}
              </button>
            ))}
            {catalog.filter(c => !registeredIds.has(c.id)).length === 0 && <span style={{ fontSize: 13, color: "#8fa3b1" }}>Ya tenés todos los servicios disponibles registrados.</span>}
          </div>
          <button onClick={() => setAdding(false)} style={{ marginTop: 10, background: "none", border: "none", color: "#8fa3b1", fontSize: 12, cursor: "pointer", fontFamily: "Montserrat, sans-serif" }}>Cancelar</button>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {services.length === 0 && !adding && <div style={{ textAlign: "center", padding: 32, color: "#8fa3b1", fontSize: 13 }}>No tenés servicios registrados. Agregá tu primer rubro.</div>}
        {services.map((s) => {
          const isEditing = !!editing[s.id];
          const draft = editing[s.id] || {};
          return (
            <div key={s.id} style={{ borderRadius: 10, border: `1.5px solid ${isEditing ? "#163244" : "#e0e9ef"}`, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: isEditing ? "#f0f5f8" : "#fafafa" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 22 }}>{SERVICE_ICONS[s.label || s.service_name] || "🛠️"}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#163244" }}>{s.label || s.service_name}</div>
                    <div style={{ fontSize: 11, color: "#8fa3b1" }}>Nivel {s.validation_level}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Badge status={s.status} />
                  {!isEditing ? (
                    <>
                      <button onClick={() => startEdit(s)} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #163244", background: "transparent", color: "#163244", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 11, cursor: "pointer" }}>✏ Editar</button>
                      <button onClick={() => handleRemove(s)} disabled={saving === s.id} style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 15, padding: "4px 6px" }} title="Eliminar">{saving === s.id ? "⏳" : "🗑"}</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => handleSaveEdit(s)} disabled={saving === s.id} style={{ padding: "5px 14px", borderRadius: 6, background: "#163244", border: "none", color: "#fff", fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: 11, cursor: saving === s.id ? "not-allowed" : "pointer" }}>{saving === s.id ? "⏳" : "Guardar cambios"}</button>
                      <button onClick={() => cancelEdit(s.id)} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #cddae4", background: "transparent", color: "#5c7a8a", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 11, cursor: "pointer" }}>Cancelar</button>
                    </>
                  )}
                </div>
              </div>
              <div style={{ padding: "14px 16px", background: "#fff", borderTop: "1px solid #f0f5f8" }}>
                {isEditing ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                    <div style={{ flex: "1 1 140px" }}>
                      <label style={labelStyle}>Años de experiencia</label>
                      <input type="number" min={0} max={50} value={draft.years_experience} onChange={e => updateDraft(s.id, "years_experience", Number(e.target.value))} style={inputStyle} />
                    </div>
                    <div style={{ flex: "1 1 180px" }}>
                      <label style={labelStyle}>Precio base referencial (ARS)</label>
                      <input type="number" min={0} value={draft.base_price_reference} onChange={e => updateDraft(s.id, "base_price_reference", e.target.value)} placeholder="Ej: 15000" style={inputStyle} />
                    </div>
                    <div style={{ flex: "1 1 160px" }}>
                      <label style={labelStyle}>Duración estimada (hs)</label>
                      <input type="number" min={0.5} max={24} step={0.5} value={draft.estimated_duration_hs} onChange={e => updateDraft(s.id, "estimated_duration_hs", e.target.value)} placeholder="Ej: 2.5" style={inputStyle} />
                      <div style={{ fontSize: 10, color: "#8fa3b1", marginTop: 3 }}>Tiempo típico que te lleva este servicio</div>
                    </div>
                    <div style={{ flex: "1 1 100%", display: "flex", gap: 24, marginTop: 4, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div onClick={() => updateDraft(s.id, "emergency_enabled", !draft.emergency_enabled)} style={{ width: 40, height: 22, borderRadius: 11, cursor: "pointer", background: draft.emergency_enabled ? "#163244" : "#c8d4dc", position: "relative", transition: "background 0.25s", flexShrink: 0 }}>
                          <div style={{ position: "absolute", top: 3, left: draft.emergency_enabled ? 20 : 3, width: 16, height: 16, borderRadius: "50%", background: draft.emergency_enabled ? "#E7A432" : "#fff", transition: "left 0.25s" }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#163244" }}>Acepta urgencias</div>
                          <div style={{ fontSize: 11, color: "#8fa3b1" }}>El cliente puede solicitarte con urgencia</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div onClick={() => updateDraft(s.id, "quote_enabled", !draft.quote_enabled)} style={{ width: 40, height: 22, borderRadius: 11, cursor: "pointer", background: draft.quote_enabled ? "#163244" : "#c8d4dc", position: "relative", transition: "background 0.25s", flexShrink: 0 }}>
                          <div style={{ position: "absolute", top: 3, left: draft.quote_enabled ? 20 : 3, width: 16, height: 16, borderRadius: "50%", background: draft.quote_enabled ? "#E7A432" : "#fff", transition: "left 0.25s" }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#163244" }}>Acepta presupuesto previo</div>
                          <div style={{ fontSize: 11, color: "#8fa3b1" }}>Podés cotizar antes de confirmar</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 13 }}><span style={{ color: "#8fa3b1" }}>Experiencia: </span><strong style={{ color: "#163244" }}>{s.years_experience || 0} años</strong></div>
                    {s.base_price_reference && <div style={{ fontSize: 13 }}><span style={{ color: "#8fa3b1" }}>Precio base: </span><strong style={{ color: "#163244" }}>${Number(s.base_price_reference).toLocaleString("es-AR")}</strong></div>}
                    {s.estimated_duration_hs && <div style={{ fontSize: 13 }}><span style={{ color: "#8fa3b1" }}>Duración típica: </span><strong style={{ color: "#163244" }}>{s.estimated_duration_hs}hs</strong></div>}
                    <div style={{ fontSize: 13 }}><span style={{ color: "#8fa3b1" }}>Urgencias: </span><strong style={{ color: s.emergency_enabled ? "#1b7f4e" : "#c0392b" }}>{s.emergency_enabled ? "✓ Sí" : "✕ No"}</strong></div>
                    <div style={{ fontSize: 13 }}><span style={{ color: "#8fa3b1" }}>Presupuesto previo: </span><strong style={{ color: s.quote_enabled ? "#1b7f4e" : "#c0392b" }}>{s.quote_enabled ? "✓ Sí" : "✕ No"}</strong></div>
                  </div>
                )}
                {s.status === "pending_documents" && <div style={{ background: "#fff3e0", border: "1px solid #E7A432", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#b06a00", marginTop: 12 }}>📄 Necesitás cargar documentación. Ir al tab <strong>Documentos</strong>.</div>}
                {s.status === "pending_review"    && <div style={{ background: "#e8f7ef", border: "1px solid #1b7f4e", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#1b7f4e", marginTop: 12 }}>✓ Documentación enviada. En revisión por el equipo de Fixo.</div>}
                {s.status === "rejected"          && <div style={{ background: "#fdecea", border: "1px solid #e57373", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#c0392b", marginTop: 12 }}>✕ Documentación rechazada. Subí documentación actualizada en el tab <strong>Documentos</strong>.</div>}
                {s.status === "expired"           && <div style={{ background: "#f3eeff", border: "1px solid #7b5ea7", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#7b5ea7", marginTop: 12 }}>⚠ Documentación vencida. Actualizá tus documentos en el tab <strong>Documentos</strong>.</div>}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

// ─── Sección cobertura ────────────────────────────────────────────────────────
function CoverageSection({ coverage: initialCoverage }) {
  const normalize = (zones) => zones.map(z => ({
    ...z,
    id:       Number(z.id),   // asegurar que el id sea siempre número
    provincia: z.provincia || z.province      || "",
    ciudad:    z.ciudad    || z.city          || "",
    barrio:    z.barrio    || z.neighborhood  || "",
    radio_km:  Number(z.radio_km || z.radius_km) || 10,
    _new:      z._new || false,
  }));

  const [zonas,    setZonas]    = useState(() => normalize(initialCoverage));
  const [saving,   setSaving]   = useState(null);
  const [error,    setError]    = useState("");
  const [editing,  setEditing]  = useState({}); // { [id]: draft }

  const addZona = () => {
    const tempId = `new-${Date.now()}`;
    setZonas(z => [...z, { id: tempId, provincia: "", ciudad: "", barrio: "", radio_km: 10, _new: true }]);
    setEditing(e => ({ ...e, [tempId]: true }));
  };

  const startEdit = (id) => setEditing(e => ({ ...e, [id]: true }));
  const cancelEdit = (zona) => {
    if (zona._new) {
      setZonas(z => z.filter(z => z.id !== zona.id));
    }
    setEditing(e => { const n = { ...e }; delete n[zona.id]; return n; });
  };

  const updateZona = (id, field, val) => {
    setZonas(z => z.map(z => z.id === id ? { ...z, [field]: val } : z));
  };

  const saveZona = async (zona) => {
    if (!zona.provincia || !zona.ciudad) {
      setError("Provincia y ciudad son obligatorias.");
      return;
    }
    setSaving(zona.id); setError("");
    try {
      const payload = {
        province:     zona.provincia,
        city:         zona.ciudad,
        neighborhood: zona.barrio || "",
        radius_km:    zona.radio_km,
      };
      if (zona._new) {
        const res = await api.post("/providers/me/coverage", payload);
        const saved = normalize([res.data.coverage])[0];
        setZonas(z => z.map(z => z.id === zona.id ? saved : z));
      } else {
        const res = await api.put(`/providers/me/coverage/${zona.id}`, payload);
        const saved = normalize([res.data.coverage])[0];
        setZonas(z => z.map(z => z.id === zona.id ? saved : z));
      }
      setEditing(e => { const n = { ...e }; delete n[zona.id]; return n; });
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar la zona.");
    } finally {
      setSaving(null);
    }
  };

  const removeZona = async (zona) => {
    if (!window.confirm("¿Eliminar esta zona de cobertura?")) return;
    setSaving(zona.id); setError("");
    try {
      if (!String(zona.id).startsWith("new-")) {
        await api.delete(`/providers/me/coverage/${zona.id}`);
      }
      setZonas(z => z.filter(z => z.id !== zona.id));
      setEditing(e => { const n = { ...e }; delete n[zona.id]; return n; });
    } catch (err) {
      setError(err.response?.data?.error || "Error al eliminar la zona.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <SectionCard
      title="Zonas de cobertura"
      subtitle="Áreas donde ofrecés tus servicios"
      action={
        <button onClick={addZona} style={{ padding: "8px 16px", borderRadius: 8, border: "2px solid #163244", background: "transparent", color: "#163244", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
          + Agregar zona
        </button>
      }
    >
      {error && (
        <div style={{ background: "#fdecea", border: "1px solid #e57373", borderRadius: 8, padding: "9px 14px", fontSize: 13, color: "#c0392b", fontWeight: 600, marginBottom: 12 }}>
          ⚠ {error}
        </div>
      )}

      {zonas.map((z, i) => {
        const isEditing = !!editing[z.id];
        return (
          <div key={z.id} style={{
            background: isEditing ? "#fffbf2" : "#f7fafc",
            borderRadius: 10, padding: "14px 16px", marginBottom: 12,
            border: `1.5px solid ${isEditing ? "#E7A432" : "#e0e9ef"}`,
          }}>
            {/* Cabecera */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isEditing ? 12 : 0 }}>
              <span style={{ fontWeight: 700, fontSize: 12, color: "#163244", background: isEditing ? "#E7A432" : "#e7f0f5", padding: "2px 10px", borderRadius: 20 }}>
                {z._new ? "Nueva zona" : `Zona ${i + 1}`}
              </span>

              {/* Vista compacta cuando no se edita */}
              {!isEditing && (
                <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, marginLeft: 12 }}>
                  <span style={{ fontSize: 13, color: "#163244", fontWeight: 600 }}>
                    {z.provincia}{z.ciudad ? `, ${z.ciudad}` : ""}{z.barrio ? ` · ${z.barrio}` : ""}
                  </span>
                  <span style={{ fontSize: 12, color: "#8fa3b1" }}>📍 {z.radio_km} km</span>
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                {isEditing ? (
                  <>
                    <button
                      onClick={() => saveZona(z)}
                      disabled={saving === z.id}
                      style={{ padding: "4px 14px", borderRadius: 6, background: "#163244", border: "none", color: "#fff", fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: 11, cursor: saving === z.id ? "not-allowed" : "pointer" }}
                    >
                      {saving === z.id ? "⏳" : "Guardar"}
                    </button>
                    <button
                      onClick={() => cancelEdit(z)}
                      disabled={saving === z.id}
                      style={{ padding: "4px 12px", borderRadius: 6, background: "transparent", border: "1px solid #cddae4", color: "#5c7a8a", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 11, cursor: "pointer" }}
                    >
                      Cancelar
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => startEdit(z.id)}
                      style={{ padding: "4px 12px", borderRadius: 6, background: "transparent", border: "1px solid #163244", color: "#163244", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 11, cursor: "pointer" }}
                    >
                      ✏ Editar
                    </button>
                    <button
                      onClick={() => removeZona(z)}
                      disabled={saving === z.id}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 15, padding: "4px 6px" }}
                      title="Eliminar zona"
                    >
                      🗑
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Formulario de edición */}
            {isEditing && (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 140px" }}>
                  <label style={labelStyle}>Provincia *</label>
                  <select style={{ ...inputStyle, cursor: "pointer" }} value={z.provincia} onChange={e => updateZona(z.id, "provincia", e.target.value)}>
                    <option value="">Seleccioná...</option>
                    {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div style={{ flex: "1 1 140px" }}>
                  <label style={labelStyle}>Ciudad *</label>
                  <input style={inputStyle} value={z.ciudad} placeholder="Ej: Neuquén" onChange={e => updateZona(z.id, "ciudad", e.target.value)} />
                </div>
                <div style={{ flex: "1 1 120px" }}>
                  <label style={labelStyle}>Barrio</label>
                  <input style={inputStyle} value={z.barrio || ""} placeholder="Opcional" onChange={e => updateZona(z.id, "barrio", e.target.value)} />
                </div>
                <div style={{ flex: "1 1 180px" }}>
                  <label style={labelStyle}>Radio de cobertura: {z.radio_km} km</label>
                  <input
                    type="range" min={1} max={100} step={1}
                    value={z.radio_km}
                    onChange={e => updateZona(z.id, "radio_km", Number(e.target.value))}
                    style={{ width: "100%", accentColor: "#E7A432", marginTop: 10 }}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#8fa3b1" }}>
                    <span>1 km</span><span>100 km</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {zonas.length === 0 && (
        <div style={{ textAlign: "center", padding: "32px", color: "#8fa3b1", background: "#f7fafc", borderRadius: 10, border: "2px dashed #e0e9ef", fontSize: 13 }}>
          📍 No tenés zonas configuradas. Agregá tu primera zona.
        </div>
      )}
    </SectionCard>
  );
}

// ─── Sección disponibilidad ───────────────────────────────────────────────────
// ─── TimeSelect: selector de hora en formato 24hs ────────────────────────────
function TimeSelect({ value, onChange }) {
  const HOURS   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  const MINUTES = ["00", "15", "30", "45"];

  const [h, m] = (value || "09:00").split(":");
  const hour   = HOURS.includes(h)   ? h   : "09";
  const minute = MINUTES.includes(m) ? m   : "00";

  const handleH = (e) => onChange(`${e.target.value}:${minute}`);
  const handleM = (e) => onChange(`${hour}:${e.target.value}`);

  const selectStyle = {
    ...inputStyle,
    width: "auto", padding: "6px 8px", fontSize: 13, cursor: "pointer",
    minWidth: 54,
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
      <select value={hour} onChange={handleH} style={selectStyle}>
        {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span style={{ color: "#8fa3b1", fontWeight: 700 }}>:</span>
      <select value={minute} onChange={handleM} style={selectStyle}>
        {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
      </select>
    </div>
  );
}

function AvailabilitySection({ availability: initialAvail, onUpdate }) {
  // Estructura base de los 7 días
  const DEFAULT_DAYS = DIAS_LABELS.map((label, i) => ({
    day_of_week: i,
    start_time:  "09:00",
    end_time:    "18:00",
    available:   false,
  }));

  // Normaliza y fusiona los datos del backend con los 7 días base
  const buildAvail = (data) => {
    if (!data || data.length === 0) return DEFAULT_DAYS;
    return DEFAULT_DAYS.map(def => {
      const match = data.find(d => (d.day_of_week ?? d.day) === def.day_of_week);
      if (!match) return def;
      return {
        ...def,
        start_time: match.start_time || match.start || "09:00",
        end_time:   match.end_time   || match.end   || "18:00",
        available:  match.available  ?? true,
      };
    });
  };

  const [avail,   setAvail]   = useState(() => buildAvail(initialAvail));
  const [loading, setLoading] = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");

  const update = (i, field, val) => {
    setAvail(a => a.map((d, idx) => idx === i ? { ...d, [field]: val } : d));
    setSaved(false);
  };

  const handleSave = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.put("/providers/me/availability", { availability: avail });
      // Actualizar el state del padre con los datos frescos del backend
      if (onUpdate && data.availability) {
        onUpdate(data.availability);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar la disponibilidad.");
    } finally {
      setLoading(false);
    }
  };

  const totalHours = avail
    .filter(d => d.available)
    .reduce((sum, d) => {
      const h = parseFloat(calcHours(d.start_time, d.end_time));
      return sum + (isNaN(h) ? 0 : h);
    }, 0);

  return (
    <SectionCard
      title="Disponibilidad semanal"
      subtitle={`${avail.filter(d => d.available).length} días activos · ${totalHours.toFixed(1)} hs semanales`}
      action={<SaveButton onClick={handleSave} loading={loading} saved={saved} />}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {avail.map((d, i) => {
          const label = DIAS_LABELS[d.day_of_week ?? i];
          return (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 10,
              background: d.available ? "#f7fafc" : "#fafafa",
              border: `1.5px solid ${d.available ? "#E7A432" : "#e0e9ef"}`,
              transition: "all 0.2s",
            }}>
              {/* Toggle */}
              <div
                onClick={() => update(i, "available", !d.available)}
                style={{
                  width: 40, height: 22, borderRadius: 11, cursor: "pointer",
                  background: d.available ? "#163244" : "#c8d4dc",
                  position: "relative", transition: "background 0.25s", flexShrink: 0,
                }}
              >
                <div style={{
                  position: "absolute", top: 3,
                  left: d.available ? 20 : 3,
                  width: 16, height: 16, borderRadius: "50%",
                  background: d.available ? "#E7A432" : "#fff",
                  transition: "left 0.25s",
                }} />
              </div>

              {/* Día */}
              <span style={{ width: 36, fontWeight: 700, fontSize: 13, color: d.available ? "#163244" : "#8fa3b1" }}>
                {label}
              </span>

              {/* Horarios */}
              {d.available ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                  <TimeSelect value={d.start_time} onChange={val => update(i, "start_time", val)} />
                  <span style={{ color: "#8fa3b1", fontSize: 12 }}>a</span>
                  <TimeSelect value={d.end_time} onChange={val => update(i, "end_time", val)} />
                  <span style={{ fontSize: 11, color: "#8fa3b1", marginLeft: "auto", whiteSpace: "nowrap" }}>
                    {calcHours(d.start_time, d.end_time)} hs
                  </span>
                </div>
              ) : (
                <span style={{ fontSize: 13, color: "#aab8c2", fontStyle: "italic" }}>No disponible</span>
              )}
            </div>
          );
        })}
      </div>
      <p style={{ fontSize: 12, color: "#8fa3b1", marginTop: 12, marginBottom: 0 }}>
        Activá los días con el toggle y configurá el horario. Luego presioná <strong>Guardar cambios</strong>.
      </p>
      <ErrorMsg msg={error} />
    </SectionCard>
  );
}

// ─── Sección documentos ───────────────────────────────────────────────────────

const CLOUDINARY_CLOUD  = 'di1nylozi';
const CLOUDINARY_PRESET = 'fixo_docs';
const MAX_FILE_MB       = 10;

const DOC_TYPES = [
  { value: 'dni',                    label: 'DNI / Identificación',                   icon: '🪪' },
  { value: 'matricula',              label: 'Matrícula profesional',                  icon: '🎓' },
  { value: 'habilitacion_municipal', label: 'Habilitación municipal',                 icon: '🏛️' },
  { value: 'seguro',                 label: 'Seguro de responsabilidad civil',        icon: '🛡️' },
  { value: 'certificado',            label: 'Certificado de gasfitería / electricidad', icon: '📋' },
  { value: 'otro',                   label: 'Otro',                                   icon: '📄' },
];

const DOC_STATUS_CONFIG = {
  pending:        { label: 'Pendiente',   color: '#b06a00', bg: '#fff3e0' },
  pending_review: { label: 'En revisión', color: '#b06a00', bg: '#fff3e0' },
  approved:       { label: 'Aprobado',    color: '#1b7f4e', bg: '#e8f7ef' },
  rejected:       { label: 'Rechazado',   color: '#c0392b', bg: '#fdecea' },
  expired:        { label: 'Vencido',     color: '#7b5ea7', bg: '#f3eeff' },
};

function DocBadge({ status }) {
  const cfg = DOC_STATUS_CONFIG[status] || { label: status, color: '#5c7a8a', bg: '#f0f5f8' };
  return (
    <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: cfg.color, background: cfg.bg }}>
      {cfg.label.toUpperCase()}
    </span>
  );
}

// Sube el archivo a Cloudinary y devuelve la URL pública
async function uploadToCloudinary(file) {
  const fd = new FormData();
  fd.append('file',         file);
  fd.append('upload_preset', CLOUDINARY_PRESET);
  fd.append('folder',       'fixo/documentos');

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/auto/upload`,
    { method: 'POST', body: fd }
  );
  if (!res.ok) throw new Error('Error al subir a Cloudinary');
  const data = await res.json();
  return data.secure_url;
}

function DocumentsSection({ services }) {
  const EMPTY_FORM = { document_type: '', provider_service_id: '', expires_at: '', file: null };

  const [documents, setDocuments] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadStep, setUploadStep] = useState(''); // 'cloudinary' | 'backend' | ''
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [showForm,  setShowForm]  = useState(false);

  const fetchDocs = async () => {
    try {
      const { data } = await api.get('/providers/me/documents');
      // El backend devuelve el array directo (no { documents: [] })
      setDocuments(Array.isArray(data) ? data : data.documents || []);
    } catch {
      setError('Error cargando documentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDocs(); }, []);

  const handleUpload = async () => {
    setError('');
    if (!form.document_type) { setError('Seleccioná el tipo de documento.'); return; }
    if (!form.file)          { setError('Seleccioná un archivo para subir.'); return; }
    if (form.file.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`El archivo supera el límite de ${MAX_FILE_MB} MB.`);
      return;
    }

    setUploading(true);
    setSuccess('');
    try {
      // 1. Subir archivo a Cloudinary
      setUploadStep('cloudinary');
      const file_url = await uploadToCloudinary(form.file);

      // 2. Registrar en el backend con los campos correctos
      setUploadStep('backend');
      await api.post('/providers/me/documents', {
        document_type:       form.document_type,
        file_url,
        provider_service_id: form.provider_service_id || null,
        expires_at:          form.expires_at           || null,
      });

      setSuccess('Documento subido correctamente. Quedará pendiente de revisión.');
      setForm(EMPTY_FORM);
      setShowForm(false);
      await fetchDocs();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al subir el documento. Intentá de nuevo.');
    } finally {
      setUploading(false);
      setUploadStep('');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    setError(''); setSuccess('');
    try {
      await api.delete(`/providers/me/documents/${docId}`);
      setSuccess('Documento eliminado.');
      await fetchDocs();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar.');
    }
  };

  const cancelForm = () => { setShowForm(false); setError(''); setForm(EMPTY_FORM); };

  const docTypeLabel = (val) => DOC_TYPES.find(d => d.value === val)?.label || val;
  const docTypeIcon  = (val) => DOC_TYPES.find(d => d.value === val)?.icon  || '📄';

  const uploadLabel = uploadStep === 'cloudinary'
    ? 'Subiendo archivo…'
    : uploadStep === 'backend'
    ? 'Registrando documento…'
    : 'Subir documento';

  return (
    <SectionCard
      title="Documentación"
      subtitle="Subí los documentos requeridos para activar tus servicios"
      action={
        !showForm && (
          <button onClick={() => setShowForm(true)} style={{
            padding: '8px 20px', borderRadius: 8, background: '#163244',
            color: '#fff', border: 'none', fontFamily: 'Montserrat, sans-serif',
            fontWeight: 600, fontSize: 13, cursor: 'pointer',
          }}>
            + Subir documento
          </button>
        )
      }
    >
      {/* Formulario de upload */}
      {showForm && (
        <div style={{ background: '#f0f5f8', borderRadius: 12, padding: 20, marginBottom: 24, border: '2px dashed #E7A432' }}>
          <div style={{ fontWeight: 700, color: '#163244', marginBottom: 16 }}>Nuevo documento</div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            {/* Tipo de documento */}
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Tipo de documento *</label>
              <select
                value={form.document_type}
                onChange={e => setForm(f => ({ ...f, document_type: e.target.value }))}
                style={inputStyle}
              >
                <option value="">Seleccioná…</option>
                {DOC_TYPES.map(d => (
                  <option key={d.value} value={d.value}>{d.icon} {d.label}</option>
                ))}
              </select>
            </div>

            {/* Servicio (opcional) */}
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Servicio (opcional)</label>
              <select
                value={form.provider_service_id}
                onChange={e => setForm(f => ({ ...f, provider_service_id: e.target.value }))}
                style={inputStyle}
              >
                <option value="">General (todos los servicios)</option>
                {(services || []).map(s => (
                  <option key={s.id} value={s.id}>
                    {s.service_name || s.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Fecha de vencimiento */}
            <div style={{ flex: '1 1 160px' }}>
              <label style={labelStyle}>Vencimiento (opcional)</label>
              <input
                type="date"
                value={form.expires_at}
                onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                style={inputStyle}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          {/* Input de archivo */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Archivo * (JPG, PNG, PDF, WEBP — máx. {MAX_FILE_MB} MB)</label>
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.pdf"
              onChange={e => setForm(f => ({ ...f, file: e.target.files[0] || null }))}
              style={{ fontSize: 13, fontFamily: 'Montserrat, sans-serif' }}
            />
            {form.file && (
              <div style={{ fontSize: 12, color: '#1b7f4e', marginTop: 4 }}>
                ✓ {form.file.name} ({(form.file.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>

          {error && (
            <div style={{ background: '#fdecea', border: '1px solid #e57373', borderRadius: 8, padding: '9px 14px', fontSize: 13, color: '#c0392b', fontWeight: 600, marginBottom: 12 }}>
              ⚠ {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleUpload} disabled={uploading} style={{
              padding: '9px 22px', borderRadius: 8,
              background: uploading ? '#8fa3b1' : '#163244',
              color: '#fff', border: 'none', fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600, fontSize: 13,
              cursor: uploading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {uploading && (
                <span style={{ width: 13, height: 13, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
              )}
              {uploading ? uploadLabel : '☁ Subir documento'}
            </button>
            <button onClick={cancelForm} disabled={uploading} style={{
              padding: '9px 22px', borderRadius: 8, background: 'transparent',
              color: '#5c7a8a', border: '1.5px solid #cddae4',
              fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: 13,
              cursor: uploading ? 'not-allowed' : 'pointer',
            }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Mensaje de éxito */}
      {success && (
        <div style={{ background: '#e8f7ef', border: '1px solid #1b7f4e', borderRadius: 8, padding: '9px 14px', fontSize: 13, color: '#1b7f4e', fontWeight: 600, marginBottom: 16 }}>
          ✓ {success}
        </div>
      )}

      {/* Lista de documentos */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 32, color: '#8fa3b1' }}>Cargando documentos…</div>
      ) : documents.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#8fa3b1' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📂</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Sin documentos cargados</div>
          <div style={{ fontSize: 13 }}>Subí tu documentación para que el equipo de Fixo pueda revisar tu perfil.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {documents.map(doc => (
            <div key={doc.id} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              background: '#f8fafc', border: '1px solid #e2eaf0',
              borderRadius: 10, padding: '12px 16px',
            }}>
              <span style={{ fontSize: 22 }}>{docTypeIcon(doc.document_type)}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#163244' }}>
                  {docTypeLabel(doc.document_type)}
                  {doc.service_name && (
                    <span style={{ fontWeight: 400, color: '#5c7a8a', marginLeft: 6, fontSize: 12 }}>
                      · {doc.service_name}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#8fa3b1', marginTop: 2 }}>
                  Subido: {new Date(doc.created_at).toLocaleDateString('es-AR')}
                  {doc.expires_at && ` · Vence: ${new Date(doc.expires_at).toLocaleDateString('es-AR')}`}
                </div>
              </div>
              {/* Campo correcto: verification_status */}
              <DocBadge status={doc.verification_status} />
              {doc.file_url && (
                <a href={doc.file_url} target="_blank" rel="noreferrer" style={{
                  padding: '5px 12px', borderRadius: 6, background: '#e3eef5',
                  color: '#163244', fontSize: 12, fontWeight: 600, textDecoration: 'none',
                }}>
                  Ver
                </a>
              )}
              {/* Solo se puede eliminar si no está aprobado */}
              {doc.verification_status !== 'approved' && (
                <button onClick={() => handleDelete(doc.id)} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#c0392b', fontSize: 16, padding: '4px 6px',
                }} title="Eliminar">
                  🗑
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}


// ─── Sección calificaciones ───────────────────────────────────────────────────
function RatingsSection({ providerId }) {
  const [ratings,  setRatings]  = useState([]);
  const [summary,  setSummary]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [replies,  setReplies]  = useState({}); // { [ratingId]: draft }
  const [saving,   setSaving]   = useState(null);
  const [success,  setSuccess]  = useState("");

  useEffect(() => {
    if (!providerId) return;
    setLoading(true);
    api.get(`/ratings/providers/provider/${providerId}?limit=50`)
      .then(res => {
        setRatings(res.data.ratings || []);
        setSummary(res.data.summary || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [providerId]);

  const startReply = (id, current) => setReplies(r => ({ ...r, [id]: current || "" }));
  const cancelReply = (id) => setReplies(r => { const n = { ...r }; delete n[id]; return n; });

  const handleSaveReply = async (ratingId) => {
    setSaving(ratingId);
    try {
      await api.patch(`/ratings/providers/reply/${ratingId}`, { reply: replies[ratingId] });
      setRatings(rs => rs.map(r => r.id === ratingId ? { ...r, provider_reply: replies[ratingId] } : r));
      cancelReply(ratingId);
      setSuccess("Respuesta guardada");
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      console.error("Error al guardar respuesta:", err);
    } finally {
      setSaving(null);
    }
  };

  const stars = (score, size = 14) => (
    <span style={{ color: "#E7A432", fontSize: size, letterSpacing: 1 }}>
      {"★".repeat(score)}
      <span style={{ color: "#e0e9ef" }}>{"★".repeat(5 - score)}</span>
    </span>
  );

  if (loading) return (
    <div style={{ textAlign: "center", padding: 40, color: "#8fa3b1", fontSize: 13 }}>
      Cargando calificaciones...
    </div>
  );

  return (
    <SectionCard
      title="Mis calificaciones"
      subtitle={summary ? `${summary.avg || "—"} promedio · ${summary.total} reseñas` : "Sin calificaciones aún"}
    >
      {success && (
        <div style={{ background: "#e8f7ef", border: "1px solid #1b7f4e", borderRadius: 8, padding: "9px 14px", fontSize: 13, color: "#1b7f4e", fontWeight: 600, marginBottom: 12 }}>
          ✓ {success}
        </div>
      )}

      {/* Resumen de estrellas */}
      {summary && parseInt(summary.total) > 0 && (
        <div style={{ background: "#f7fafc", borderRadius: 12, padding: "16px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 24 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: "#163244" }}>{summary.avg || "—"}</div>
            <div style={{ marginTop: 4 }}>{stars(Math.round(parseFloat(summary.avg) || 0), 18)}</div>
            <div style={{ fontSize: 11, color: "#8fa3b1", marginTop: 4 }}>{summary.total} reseña{summary.total !== "1" ? "s" : ""}</div>
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
            {[
              { stars: 5, count: summary.five_stars },
              { stars: 4, count: summary.four_stars },
              { stars: 3, count: summary.three_stars },
              { stars: 2, count: parseInt(summary.low_stars || 0) > 0 ? "1+" : 0 },
            ].map(row => {
              const pct = summary.total > 0 ? (parseInt(row.count) / parseInt(summary.total)) * 100 : 0;
              return (
                <div key={row.stars} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
                  <span style={{ color: "#8fa3b1", width: 14, textAlign: "right" }}>{row.stars}</span>
                  <span style={{ color: "#E7A432", fontSize: 10 }}>★</span>
                  <div style={{ flex: 1, height: 6, background: "#e0e9ef", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: "#E7A432", borderRadius: 3 }} />
                  </div>
                  <span style={{ color: "#8fa3b1", width: 20 }}>{row.count || 0}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Lista de reseñas */}
      {ratings.length === 0 ? (
        <div style={{ textAlign: "center", padding: 32, color: "#8fa3b1", fontSize: 13 }}>
          Todavía no recibiste ninguna calificación.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {ratings.map(r => {
            const isReplying = replies[r.id] !== undefined;
            return (
              <div key={r.id} style={{ borderRadius: 10, border: "1.5px solid #e0e9ef", overflow: "hidden" }}>
                {/* Header */}
                <div style={{ padding: "12px 16px", background: "#fafafa", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#163244", marginBottom: 3 }}>
                      {r.client_name}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {stars(r.score)}
                      <span style={{ fontSize: 11, color: "#8fa3b1" }}>
                        · {r.service_key} · {new Date(r.created_at).toLocaleDateString("es-AR")}
                      </span>
                    </div>
                  </div>
                  {!isReplying && (
                    <button
                      onClick={() => startReply(r.id, r.provider_reply)}
                      style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #163244", background: "transparent", color: "#163244", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 11, cursor: "pointer" }}
                    >
                      {r.provider_reply ? "✏ Editar respuesta" : "Responder"}
                    </button>
                  )}
                </div>

                <div style={{ padding: "12px 16px", background: "#fff" }}>
                  {/* Comentario del cliente */}
                  {r.comment && (
                    <div style={{ fontSize: 13, color: "#5c7a8a", lineHeight: 1.6, marginBottom: r.provider_reply || isReplying ? 12 : 0 }}>
                      "{r.comment}"
                    </div>
                  )}
                  {!r.comment && !r.provider_reply && !isReplying && (
                    <div style={{ fontSize: 12, color: "#aab8c2", fontStyle: "italic" }}>Sin comentario.</div>
                  )}

                  {/* Respuesta existente */}
                  {r.provider_reply && !isReplying && (
                    <div style={{ background: "#f0f5f8", borderRadius: 8, padding: "10px 14px", borderLeft: "3px solid #163244", fontSize: 12, color: "#163244", lineHeight: 1.6 }}>
                      <span style={{ fontWeight: 700, fontSize: 11, color: "#8fa3b1", display: "block", marginBottom: 4 }}>TU RESPUESTA</span>
                      {r.provider_reply}
                    </div>
                  )}

                  {/* Editor de respuesta */}
                  {isReplying && (
                    <div>
                      <textarea
                        style={{ ...inputStyle, minHeight: 72, resize: "vertical", lineHeight: 1.6, fontSize: 13 }}
                        placeholder="Escribi tu respuesta..."
                        value={replies[r.id]}
                        onChange={e => setReplies(rv => ({ ...rv, [r.id]: e.target.value }))}
                      />
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <button
                          onClick={() => handleSaveReply(r.id)}
                          disabled={saving === r.id || !replies[r.id]?.trim()}
                          style={{ padding: "6px 16px", borderRadius: 6, background: "#163244", border: "none", color: "#fff", fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                        >
                          {saving === r.id ? "Guardando..." : "Guardar respuesta"}
                        </button>
                        <button
                          onClick={() => cancelReply(r.id)}
                          style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #cddae4", background: "transparent", color: "#5c7a8a", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────
function LoadingSkeleton() {
  return (
    <div style={{ minHeight: "100vh", background: "#f0f5f8", fontFamily: "Montserrat, sans-serif" }}>
      <div style={{ background: "#163244", height: 56 }} />
      <div style={{ background: "#E7A432", height: 5 }} />
      <div style={{ maxWidth: 820, margin: "0 auto", padding: "32px 20px" }}>
        <div style={{ background: "#163244", borderRadius: 16, height: 120, marginBottom: 20, opacity: 0.3 }} />
        <div style={{ background: "#fff", borderRadius: 12, height: 48, marginBottom: 20, opacity: 0.5 }} />
        <div style={{ background: "#fff", borderRadius: 16, height: 200, opacity: 0.5 }} />
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProviderProfilePage() {
  const [provider,  setProvider]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [activeTab, setActiveTab] = useState("perfil");

  // Cargar perfil desde GET /providers/me
  useEffect(() => {
    api.get("/providers/me")
      .then(res => {
        // El backend puede devolver { provider: {...} } o el objeto directo
        const data = res.data.provider || res.data;
        // Normalizar availability para que tenga label
        if (data.availability) {
          data.availability = data.availability.map(a => ({
            ...a,
            label: DIAS_LABELS[a.day_of_week ?? a.day ?? 0],
          }));
        }
        setProvider(data);
      })
      .catch(err => {
        console.error("Error cargando perfil:", err);
        setError("No se pudo cargar el perfil. Intentá recargar la página.");
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSkeleton />;

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: "#f0f5f8", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Montserrat, sans-serif" }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: 40, textAlign: "center", maxWidth: 400 }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
          <div style={{ fontWeight: 700, color: "#163244", marginBottom: 8 }}>Error al cargar el perfil</div>
          <div style={{ color: "#5c7a8a", fontSize: 14, marginBottom: 20 }}>{error}</div>
          <button onClick={() => window.location.reload()} style={{ background: "#163244", color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontFamily: "Montserrat, sans-serif", fontWeight: 700, cursor: "pointer" }}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const p = provider;

  const TABS = [
    { id: "perfil",           label: "Perfil",           icon: "👤" },
    { id: "servicios",        label: "Servicios",         icon: "🛠️" },
    { id: "cobertura",        label: "Cobertura",         icon: "📍" },
    { id: "disponibilidad",   label: "Disponibilidad",    icon: "📅" },
    { id: "documentos",       label: "Documentos",        icon: "📄" },
    { id: "calificaciones",   label: "Calificaciones",    icon: "⭐" },
  ];

  return (
    <div style={{ fontFamily: "Montserrat, sans-serif" }}>

      <div style={{ maxWidth: 820, margin: "0 auto", padding: "32px 20px 80px" }}>

        {/* Header card */}
        <div style={{ background: "#163244", borderRadius: 16, padding: "28px 32px", marginBottom: 24, display: "flex", alignItems: "center", gap: 24, boxShadow: "0 4px 20px rgba(22,50,68,0.18)" }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#E7A432", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800, color: "#163244", flexShrink: 0 }}>
            {p.display_name?.[0]}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <h1 style={{ margin: 0, color: "#fff", fontSize: 20, fontWeight: 800 }}>{p.display_name}</h1>
              <Badge status={p.status} />
            </div>
            <div style={{ color: "#8fa3b1", fontSize: 13 }}>{p.legal_name} · DNI {p.dni}</div>
          </div>
          <div style={{ display: "flex", gap: 24, textAlign: "center" }}>
            <div>
              <div style={{ color: "#E7A432", fontWeight: 800, fontSize: 22 }}>⭐ {p.rating_avg || "—"}</div>
              <div style={{ color: "#8fa3b1", fontSize: 11 }}>Calificación</div>
            </div>
            <div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 22 }}>{p.jobs_completed || 0}</div>
              <div style={{ color: "#8fa3b1", fontSize: 11 }}>Trabajos</div>
            </div>
            <div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 22 }}>
                {(p.services || []).filter(s => s.status === "approved").length}/{(p.services || []).length}
              </div>
              <div style={{ color: "#8fa3b1", fontSize: 11 }}>Servicios</div>
            </div>
          </div>
        </div>

        {/* Verificaciones */}
        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          {[
            { label: "Identidad verificada", ok: p.identity_verified, icon: "🪪" },
            { label: "Teléfono verificado",  ok: p.phone_verified,    icon: "📱" },
          ].map(v => (
            <div key={v.label} style={{ flex: 1, background: v.ok ? "#e8f7ef" : "#fdecea", border: `1px solid ${v.ok ? "#1b7f4e" : "#e57373"}`, borderRadius: 10, padding: "10px 16px", display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: v.ok ? "#1b7f4e" : "#c0392b", fontWeight: 600 }}>
              {v.icon} {v.label}
              <span style={{ marginLeft: "auto", fontWeight: 800 }}>{v.ok ? "✓" : "✕"}</span>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20, background: "#fff", borderRadius: 12, padding: 6, boxShadow: "0 2px 8px rgba(22,50,68,0.06)" }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              flex: 1, padding: "10px 8px", borderRadius: 8, border: "none",
              background: activeTab === t.id ? "#163244" : "transparent",
              color: activeTab === t.id ? "#fff" : "#5c7a8a",
              fontFamily: "Montserrat, sans-serif",
              fontWeight: activeTab === t.id ? 700 : 500,
              fontSize: 13, cursor: "pointer", transition: "all 0.2s",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <span>{t.icon}</span><span>{t.label}</span>
            </button>
          ))}
        </div>

        {/* Contenido */}
        {activeTab === "perfil"         && <>
          <ProfileSection    data={p} onUpdate={updated => setProvider(prev => ({ ...prev, ...updated }))} />
          <PersonalDataSection />
        </>}
        {activeTab === "servicios"      && <ServicesSection     services={p.services || []} />}
        {activeTab === "cobertura"      && <CoverageSection     coverage={p.coverage || p.coverage_areas || []} />}
        {activeTab === "disponibilidad" && <AvailabilitySection availability={p.availability || []} onUpdate={updated => setProvider(prev => ({ ...prev, availability: updated }))} />}
        {activeTab === "documentos"     && <DocumentsSection    services={p.services || []} />}
        {activeTab === "calificaciones" && <RatingsSection     providerId={p.id} />}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        input:focus, select:focus, textarea:focus { border-color: #163244 !important; box-shadow: 0 0 0 3px rgba(22,50,68,0.08); }
        input[type="time"]::-webkit-datetime-edit-ampm-field { display: none; }
        input[type="time"] { font-variant-numeric: tabular-nums; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
